import React, { useState, useMemo } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter";
import { SimplifyModifier } from "three/examples/jsm/modifiers/SimplifyModifier";
import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils"; // mergeVertices(geom, tolerance)

/**
 * ReductorSTL avanzado
 *
 * Técnicas incluidas:
 * - SimplifyModifier (decimación por porcentaje)
 * - Escalado (scale)
 * - Merge / Weld vertices (mergeVertices con tolerancia)
 * - Quantize / precision reduction (redondear coordenadas a N decimales)
 * - Vertex clustering (voxel / grid clustering)
 * - Remove degenerate faces (área muy pequeña o vértices repetidos)
 * - Recompute normals
 * - Export ASCII / Binary STL
 *
 * Resultado: lista de variantes con métricas + previsualización y descarga.
 */

export default function ReductorSTL() {
  const [originalGeometry, setOriginalGeometry] = useState(null);
  const [previewGeometry, setPreviewGeometry] = useState(null);
  const [variants, setVariants] = useState([]);
  const [showSideBySide, setShowSideBySide] = useState(false);

  // --- helpers métricas ---
  const getMetrics = (geom) => {
    if (!geom) return { vertices: 0, triangles: 0 };
    const pos = geom.attributes.position;
    const idx = geom.index;
    const vertices = pos ? pos.count : 0;
    const triangles = idx ? Math.floor(idx.count / 3) : Math.floor((pos.count || 0) / 3);
    return { vertices, triangles };
  };

  // --- conversor geometry -> blob (ASCII o binary) ---
  const geometryToBlob = (geometry, { binary = false } = {}) => {
    const exporter = new STLExporter();
    const parsed = exporter.parse(new THREE.Mesh(geometry), { binary });
    if (parsed instanceof ArrayBuffer || parsed.buffer instanceof ArrayBuffer) {
      return new Blob([parsed], { type: binary ? "application/octet-stream" : "application/octet-stream" });
    } else {
      // ASCII string
      return new Blob([parsed], { type: "text/plain" });
    }
  };

  // --- descarga ---
  const descargarBlob = (blob, nombre) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre;
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- util: eliminar triángulos degenerados (área ~ 0) y construir geom indexada ---
  const removeDegenerateAndReindex = (geom, areaEpsilon = 1e-8) => {
    // Convertir a no indexado si hace falta para iterar triángulos
    let nonIndexed = geom.index ? geom.toNonIndexed() : geom.clone();
    const pos = nonIndexed.attributes.position.array;
    const newPositions = [];
    const newIndices = [];
    const mapVertexToIndex = new Map();
    let cursor = 0;

    const v = (i) => new THREE.Vector3(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]);

    for (let t = 0; t < pos.length / 9; t++) {
      const i0 = t * 3 + 0;
      const i1 = t * 3 + 1;
      const i2 = t * 3 + 2;
      const p0 = v(i0);
      const p1 = v(i1);
      const p2 = v(i2);
      const area = new THREE.Triangle(p0, p1, p2).getArea();
      if (area <= areaEpsilon) continue; // degenerado -> saltar

      // For each vertex, add to map unique by position string
      const handleVertex = (vec) => {
        const key = `${vec.x.toPrecision(12)}_${vec.y.toPrecision(12)}_${vec.z.toPrecision(12)}`;
        if (mapVertexToIndex.has(key)) return mapVertexToIndex.get(key);
        const idx = cursor++;
        mapVertexToIndex.set(key, idx);
        newPositions.push(vec.x, vec.y, vec.z);
        return idx;
      };

      const a = handleVertex(p0);
      const b = handleVertex(p1);
      const c = handleVertex(p2);

      if (a === b || b === c || a === c) continue; // tri degenerate after dedupe
      newIndices.push(a, b, c);
    }

    const newGeom = new THREE.BufferGeometry();
    newGeom.setAttribute("position", new THREE.BufferAttribute(new Float32Array(newPositions), 3));
    newGeom.setIndex(newIndices);
    newGeom.computeVertexNormals();
    return newGeom;
  };

  // --- util: quantize positions (reduce precisión) ---
  const quantizeGeometry = (geom, decimals = 3) => {
    const g = geom.clone();
    const posAttr = g.attributes.position;
    const arr = new Float32Array(posAttr.array.length);
    const factor = Math.pow(10, decimals);
    for (let i = 0; i < posAttr.array.length; i++) {
      arr[i] = Math.round(posAttr.array[i] * factor) / factor;
    }
    g.setAttribute("position", new THREE.BufferAttribute(arr, 3));
    // Eliminar índices repetidos: convertir a indexed luego mergeVertices
    try {
      g.computeBoundingSphere();
      g.computeVertexNormals();
    } catch (e) {}
    return g;
  };

  // --- util: vertex clustering (voxel) ---
  const clusterGeometry = (geom, voxelSize = 1.0) => {
    // Assumimos geom no indexado o indexado -> convertimos a no indexado para iterar triángulos
    const nonIndexed = geom.index ? geom.toNonIndexed() : geom.clone();
    const pos = nonIndexed.attributes.position.array;
    const map = new Map();
    const clusters = [];
    const triangles = [];

    function keyFor(x, y, z) {
      const xi = Math.floor(x / voxelSize);
      const yi = Math.floor(y / voxelSize);
      const zi = Math.floor(z / voxelSize);
      return `${xi}_${yi}_${zi}`;
    }

    // recorrer vértices por triángulo (cada 3 puntos)
    for (let i = 0; i < pos.length; i += 9) {
      const triIdx = [];
      for (let v = 0; v < 3; v++) {
        const x = pos[i + v * 3 + 0];
        const y = pos[i + v * 3 + 1];
        const z = pos[i + v * 3 + 2];
        const k = keyFor(x, y, z);
        if (!map.has(k)) map.set(k, { sum: new THREE.Vector3(x, y, z), count: 1, index: map.size });
        else {
          const o = map.get(k);
          o.sum.add(new THREE.Vector3(x, y, z));
          o.count++;
        }
        triIdx.push(k);
      }
      triangles.push(triIdx);
    }

    // build new vertices array based on cluster map
    const keys = Array.from(map.keys());
    const indexOf = new Map();
    const positions = new Float32Array(keys.length * 3);
    keys.forEach((k, idx) => {
      const o = map.get(k);
      const avg = o.sum.clone().multiplyScalar(1 / o.count);
      positions[idx * 3 + 0] = avg.x;
      positions[idx * 3 + 1] = avg.y;
      positions[idx * 3 + 2] = avg.z;
      indexOf.set(k, idx);
    });

    // triangles -> indices
    const indices = [];
    triangles.forEach((tri) => {
      const a = indexOf.get(tri[0]);
      const b = indexOf.get(tri[1]);
      const c = indexOf.get(tri[2]);
      if (a === b || b === c || a === c) return; // degenerate
      indices.push(a, b, c);
    });

    const newGeom = new THREE.BufferGeometry();
    newGeom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    newGeom.setIndex(indices);
    newGeom.computeVertexNormals();
    return newGeom;
  };

  // --- generar variantes (todas las técnicas) ---
  const generarVariantes = (geometry) => {
    if (!geometry) return;
    const base = geometry.clone();
    base.computeVertexNormals();
    base.center();

    const out = [];

    // Variante: original (ASCII y BIN)
    try {
      const blobAscii = geometryToBlob(base, { binary: false });
      out.push({
        name: "Original (ASCII)",
        geometry: base.clone(),
        blob: blobAscii,
        sizeKB: (blobAscii.size / 1024).toFixed(1),
        metrics: getMetrics(base),
        binary: false,
      });
      const blobBin = geometryToBlob(base, { binary: true });
      out.push({
        name: "Original (Binary)",
        geometry: base.clone(),
        blob: blobBin,
        sizeKB: (blobBin.size / 1024).toFixed(1),
        metrics: getMetrics(base),
        binary: true,
      });
    } catch (err) {
      console.warn("export original failed", err);
    }

    // 1) SimplifyModifier (decimación) -> varios niveles
    try {
      const modifier = new SimplifyModifier();
      const simplLevels = [0.1, 0.25, 0.5, 0.75]; // porcentajes de reducción
      simplLevels.forEach((level) => {
        // count = vertices * (1 - level) -> Simplify expects target count of vertices
        const nonIndexed = base.toNonIndexed();
        const originalCount = nonIndexed.attributes.position.count;
        const target = Math.max(3, Math.floor(originalCount * (1 - level)));
        const reduced = modifier.modify(nonIndexed, target);
        reduced.computeVertexNormals();
        const blob = geometryToBlob(reduced, { binary: true });
        out.push({
          name: `Simplify -${Math.round(level * 100)}% (decimate)`,
          geometry: reduced,
          blob,
          sizeKB: (blob.size / 1024).toFixed(1),
          metrics: getMetrics(reduced),
          params: { level },
        });
      });
    } catch (e) {
      console.warn("simplify failed", e);
    }

    // 2) Merge / weld vertices con tolerancia
    try {
      const tolerances = [1e-4, 1e-3, 1e-2];
      tolerances.forEach((tol) => {
        const g = base.clone();
        // mergeVertices from BufferGeometryUtils expects non-indexed? it works with indexed too
        const merged = mergeVertices(g, tol);
        merged.computeVertexNormals();
        const blob = geometryToBlob(merged, { binary: true });
        out.push({
          name: `MergeVertices tol=${tol}`,
          geometry: merged,
          blob,
          sizeKB: (blob.size / 1024).toFixed(1),
          metrics: getMetrics(merged),
          params: { tol },
        });
      });
    } catch (e) {
      console.warn("mergeVertices failed", e);
    }

    // 3) Quantize / Precision reduction (decimales)
    const decimalsList = [4, 3, 2];
    decimalsList.forEach((dec) => {
      const q = quantizeGeometry(base, dec);
      const qClean = removeDegenerateAndReindex(q, 1e-9);
      const blob = geometryToBlob(qClean, { binary: true });
      out.push({
        name: `Quantize decimals=${dec}`,
        geometry: qClean,
        blob,
        sizeKB: (blob.size / 1024).toFixed(1),
        metrics: getMetrics(qClean),
        params: { dec },
      });
    });

    // 4) Vertex Clustering (voxel)
    const voxelSizes = [0.5, 1.0, 2.0]; // ajusta según unidad del modelo
    voxelSizes.forEach((vsize) => {
      try {
        const cl = clusterGeometry(base, vsize);
        const clClean = removeDegenerateAndReindex(cl, 1e-9);
        const blob = geometryToBlob(clClean, { binary: true });
        out.push({
          name: `Voxel cluster size=${vsize}`,
          geometry: clClean,
          blob,
          sizeKB: (blob.size / 1024).toFixed(1),
          metrics: getMetrics(clClean),
          params: { vsize },
        });
      } catch (e) {
        console.warn("cluster failed", e);
      }
    });

    // 5) Scaling (reduce dimensiones físicas — no necesariamente reduce triángulos)
    const scales = [0.9, 0.75, 0.5, 0.25];
    scales.forEach((s) => {
      const sc = base.clone();
      sc.scale(s, s, s);
      sc.computeVertexNormals();
      const blob = geometryToBlob(sc, { binary: true });
      out.push({
        name: `Scale x${s}`,
        geometry: sc,
        blob,
        sizeKB: (blob.size / 1024).toFixed(1),
        metrics: getMetrics(sc),
        params: { s },
      });
    });

    // 6) Small cleanup: remove degenerate triangles + reindex
    try {
      const cleaned = removeDegenerateAndReindex(base, 1e-8);
      const blob = geometryToBlob(cleaned, { binary: true });
      out.push({
        name: `Remove degenerate & reindex`,
        geometry: cleaned,
        blob,
        sizeKB: (blob.size / 1024).toFixed(1),
        metrics: getMetrics(cleaned),
      });
    } catch (e) {
      console.warn("cleanup failed", e);
    }

    // 7) Export Binary (already added originals, but una variante final combinada)
    try {
      const combinedFinal = removeDegenerateAndReindex(base, 1e-9);
      const mergedFinal = mergeVertices(combinedFinal, 1e-4);
      mergedFinal.computeVertexNormals();
      const blob = geometryToBlob(mergedFinal, { binary: true });
      out.push({
        name: `Final combinado (merged, cleaned, binary)`,
        geometry: mergedFinal,
        blob,
        sizeKB: (blob.size / 1024).toFixed(1),
        metrics: getMetrics(mergedFinal),
      });
    } catch (e) {
      console.warn("final combine failed", e);
    }

    // ordenar por tamaño (opcional) — útil para mostrar las mejores opciones arriba
    out.sort((a, b) => parseFloat(a.sizeKB) - parseFloat(b.sizeKB));

    setVariants(out);
    // preview default = original
    setOriginalAndPreview(base, out.length ? out[0].geometry.clone() : base.clone());
  };

  const setOriginalAndPreview = (orig, preview) => {
    setOriginalGeometry(orig);
    setPreviewGeometry(preview);
  };

  // --- archivo subido ---
  const handleFileUpload = (ev) => {
    const file = ev.target.files[0];
    if (!file) return;
    const loader = new STLLoader();
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const geom = loader.parse(e.target.result);
        geom.computeVertexNormals();
        geom.center();
        generarVariantes(geom);
      } catch (err) {
        console.error("Error parse STL", err);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // --- Modelo 3D simple para preview ---
  const ModelMesh = ({ geometry, color = "#00bfff" }) => {
    if (!geometry) return null;
    return (
      <mesh geometry={geometry}>
        <meshStandardMaterial color={color} metalness={0.3} roughness={0.5} />
      </mesh>
    );
  };

  // --- UI ---
  return (
    <div style={{ padding: 14, color: "#fff", fontFamily: "sans-serif" }}>
      <h2>🧩 Reductor / Optimizador STL — Avanzado</h2>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
        <input type="file" accept=".stl" onChange={handleFileUpload} />
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="checkbox"
            checked={showSideBySide}
            onChange={(e) => setShowSideBySide(e.target.checked)}
          />
          Mostrar original + variante (lado a lado)
        </label>
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1, minHeight: 360, border: "1px solid #444", borderRadius: 8, background: "#111" }}>
          <div style={{ height: 360 }}>
            <Canvas camera={{ position: [0, 0, 120] }}>
              <ambientLight />
              <pointLight position={[100, 100, 100]} />
              <OrbitControls />
              {/* si se quiere lado a lado puedes renderizar original en una esquina, pero aquí mostramos preview */}
              {previewGeometry && <ModelMesh geometry={previewGeometry} color={"#00bfff"} />}
            </Canvas>
          </div>
        </div>

        {showSideBySide && (
          <div style={{ width: 360, minHeight: 360, border: "1px solid #444", borderRadius: 8, background: "#0b0b0b" }}>
            <div style={{ padding: 8, color: "#ddd" }}>
              <h4 style={{ margin: 0 }}>Original</h4>
              <div style={{ height: 300 }}>
                <Canvas camera={{ position: [0, 0, 120] }}>
                  <ambientLight />
                  <pointLight position={[100, 100, 100]} />
                  <OrbitControls />
                  {originalGeometry && <ModelMesh geometry={originalGeometry} color={"#ff8c42"} />}
                </Canvas>
              </div>
              <div style={{ marginTop: 6 }}>
                {originalGeometry && (
                  <>
                    <small>
                      Vtx: {getMetrics(originalGeometry).vertices} • Tri: {getMetrics(originalGeometry).triangles}
                    </small>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* variantes */}
      <div style={{ marginTop: 14 }}>
        <h3>Opciones generadas ({variants.length})</h3>

        {variants.length === 0 && <div style={{ color: "#bbb" }}>Carga un STL para generar opciones automáticas.</div>}

        {variants.map((v, i) => {
          const m = v.metrics || getMetrics(v.geometry);
          return (
            <div
              key={v.name + i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                padding: 10,
                marginTop: 8,
                borderRadius: 8,
                background: "#161616",
                border: "1px solid #2a2a2a",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{v.name}</div>
                <div style={{ fontSize: 12, color: "#bbb" }}>
                  Vtx: {m.vertices} • Tri: {m.triangles} • Tamaño: {v.sizeKB} KB
                </div>
                {v.params && (
                  <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>{JSON.stringify(v.params)}</div>
                )}
              </div>

              <div style={{ display: "flex", gap: 6 }}>
                <button
                  onClick={() => {
                    setPreviewGeometry(v.geometry.clone());
                  }}
                >
                  Ver
                </button>

                <button
                  onClick={() => {
                    // descargar en el formato que trae la variante (normalmente binario)
                    descargarBlob(v.blob, `${v.name.replace(/\s+/g, "_")}.stl`);
                  }}
                >
                  Descargar
                </button>

                <button
                  onClick={() => {
                    // probar export ascii también
                    try {
                      const bAscii = geometryToBlob(v.geometry, { binary: false });
                      descargarBlob(bAscii, `${v.name.replace(/\s+/g, "_")}_ASCII.stl`);
                    } catch (e) {
                      alert("No se pudo exportar ASCII: " + e.message);
                    }
                  }}
                >
                  Descargar ASCII
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 16, color: "#ccc", fontSize: 13 }}>
        <strong>Notas:</strong>
        <ul>
          <li>
            La decimación (SimplifyModifier) es la técnica principal para reducir polígonos sin cambiar drásticamente la forma.
          </li>
          <li>Quantize / merge / voxel clustering son técnicas complementarias que suelen bajar tamaño y simplificar geometría.</li>
          <li>El export BIN suele ser mucho más pequeño que ASCII.</li>
          <li>Para reparaciones complejas (remesh, hole-fill, boolean, análisis topológico) lo ideal es usar herramientas offline (MeshLab, Blender, Instant Meshes, CGAL).</li>
        </ul>
      </div>
    </div>
  );
}
